import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BackArrow, TopProgress } from "../../../../components";
import { utilsService } from "../../../../api";
import { useUi } from "../../../../context/ui.context";
import { usePageTitle } from "../../../../context/navigationContext";
import {
  Container,
  WrapperRow,
  Title,
  BodyText,
} from "../../../cloud/Instances/styles";
import { timeConverterMMDDYYHHMM } from "../../../../utils/dates";
import {
  EmptyValue,
  Label,
  LinkValue,
  SecondaryValue,
  Stack,
  UserInfo,
  UserLink,
  UserMeta,
  UserName,
  Value,
} from "../styles";
import {
  InfoGrid,
  InfoItem,
  MessageBody,
  Section,
  SectionTitle,
} from "./styles";

type EmailMessageLogDetails = {
  id?: number;
  subject?: string | null;
  created_at?: string | null;
  to?: string | null;
  cc?: string | null;
  body?: string | null;
  enduser_recipients?: string | null;
  staff_recipients?: string | null;
  status?: string | null;
  error_message?: string | null;
  client?: {
    id?: number;
    name?: string | null;
    display?: string | null;
  } | null;
  client_name?: string | null;
  user?: {
    id?: number;
    full_name?: string | null;
    email?: string | null;
  } | null;
  user_name?: string | null;
  user_email?: string | null;
};

const EmailMessageLogDetailsPage: FC = () => {
  const { logId } = useParams();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  usePageTitle(
    t("PAGE_TITLE_EMAIL_LOG", { defaultValue: t("EMAIL_MESSAGE_LOG") })
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [log, setLog] = useState<EmailMessageLogDetails | null>(null);

  const renderClient = useCallback(
    (item?: EmailMessageLogDetails | null) => {
      const client = item?.client;
      const name = client?.display || client?.name || item?.client_name;

      if (!name && !client?.id) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <Stack>
          {name ? (
            client?.id ? (
              <LinkValue to={`/clients-users/clients/${client.id}/details`} title={name}>
                {name}
              </LinkValue>
            ) : (
              <Value title={name}>{name}</Value>
            )
          ) : null}
          {client?.id ? <SecondaryValue>#{client.id}</SecondaryValue> : null}
        </Stack>
      );
    },
    [t]
  );

  const renderUser = useCallback(
    (item?: EmailMessageLogDetails | null) => {
      const user = item?.user;
      const name = user?.full_name || user?.email || item?.user_name;
      const email = user?.email || item?.user_email;

      if (!name && !email && !user?.id) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      const meta: string[] = [];
      if (email) {
        meta.push(email);
      }
      if (user?.id) {
        meta.push(`#${user.id}`);
      }

      return (
        <UserInfo>
          {name ? (
            user?.id ? (
              <UserLink to={`/clients-users/users/${user.id}/overview`} title={name}>
                {name}
              </UserLink>
            ) : (
              <UserName title={name}>{name}</UserName>
            )
          ) : null}
          {meta.length > 0 ? (
            <UserMeta>
              {meta.map((value, index) => (
                <span key={`${value}-${index}`}>{value}</span>
              ))}
            </UserMeta>
          ) : null}
        </UserInfo>
      );
    },
    [t]
  );

  const fetchLog = useCallback(() => {
    if (!logId) {
      navigate("/utilities/email-message-log");
      return;
    }

    setLoading(true);
    utilsService
      .getEmailMessageLogEntry(logId)
      .then((res) => {
        setLog(res.data || null);
      })
      .catch((error) => {
        console.error("Failed to fetch email message log details", error);
        viewAlert({
          severity: "error",
          message:
            error?.response?.data?.detail || t("ERROR_OCCURRED"),
        });
        navigate("/utilities/email-message-log");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [logId, navigate, t, viewAlert]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const generalInfo = useMemo(
    () => [
      {
        label: "ID",
        value: log?.id ? (
          <Value title={String(log.id)}>{log.id}</Value>
        ) : (
          <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>
        ),
      },
      {
        label: t("SUBJECT"),
        value: log?.subject ? (
          <Value title={log.subject}>{log.subject}</Value>
        ) : (
          <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>
        ),
      },
      {
        label: t("CREATED_AT"),
        value: log?.created_at ? (
          timeConverterMMDDYYHHMM(log.created_at)
        ) : (
          <EmptyValue>—</EmptyValue>
        ),
      },
      {
        label: t("CLIENT"),
        value: renderClient(log),
      },
      {
        label: t("USER"),
        value: renderUser(log),
      },
      {
        label: t("ERROR"),
        value: log?.error_message ? (
          <Value title={log.error_message}>{log.error_message}</Value>
        ) : (
          <EmptyValue>—</EmptyValue>
        ),
      },
    ],
    [log, renderClient, renderUser, t]
  );

  const recipientInfo = useMemo(
    () => [
      {
        label: t("TO"),
        value: log?.to || log?.enduser_recipients,
      },
      {
        label: t("CC"),
        value: log?.cc,
      },
      {
        label: t("ENDUSER_RECIPIENTS", { defaultValue: "To end-users" }),
        value: log?.enduser_recipients,
      },
      {
        label: t("STAFF_RECIPIENTS", { defaultValue: "To staff users" }),
        value: log?.staff_recipients,
      },
    ].filter((item) => item.value),
    [log, t]
  );

  return (
    <Container>
      {loading ? <TopProgress loading={loading} /> : null}
      <BackArrow fallbackPath="/utilities/email-message-log" />
      <WrapperRow>
        <Title>{log?.subject || t("EMAIL_MESSAGE_LOG")}</Title>
      </WrapperRow>
      {loading ? (
        <WrapperRow style={{ marginTop: "20px" }}>
          <BodyText>{t("LOADING")}...</BodyText>
        </WrapperRow>
      ) : log ? (
        <>
          <Section>
            <SectionTitle>{t("GENERAL_INFORMATION")}</SectionTitle>
            <InfoGrid>
              {generalInfo.map((item) => (
                <InfoItem key={item.label}>
                  <Label>{item.label}</Label>
                  {item.value}
                </InfoItem>
              ))}
            </InfoGrid>
          </Section>
          {recipientInfo.length > 0 ? (
            <Section>
              <SectionTitle>{t("RECIPIENTS")}</SectionTitle>
              <InfoGrid>
                {recipientInfo.map((item) => (
                  <InfoItem key={item.label}>
                    <Label>{item.label}</Label>
                    <Value>{item.value}</Value>
                  </InfoItem>
                ))}
              </InfoGrid>
            </Section>
          ) : null}
          <Section>
            <SectionTitle>{t("MESSAGE", { defaultValue: "Message" })}</SectionTitle>
            {log.body ? (
              <MessageBody dangerouslySetInnerHTML={{ __html: log.body }} />
            ) : (
              <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>
            )}
          </Section>
        </>
      ) : (
        <WrapperRow style={{ marginTop: "20px" }}>
          <BodyText>{t("NO_EMAIL_MESSAGE_LOGS")}</BodyText>
        </WrapperRow>
      )}
    </Container>
  );
};

export default EmailMessageLogDetailsPage;
